import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { CheckCircle, Warning } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("No reset token — the link appears to be invalid.");
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="reset-password-page">
      <SEO title="Set a new password" description="Set your new Point Penta account password." path="/reset-password" noIndex />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-6 w-full">
        <Link to="/" className="flex items-center gap-3" data-testid="reset-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_penta-realty-uae/artifacts/lk46xfkn_Point%20Penta%20Logo%20png.png"
            alt="Point Penta"
            className="h-11 w-auto"
          />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md border border-[#e5e7eb] p-10 bg-white">
          {!token ? (
            <div>
              <Warning size={40} className="text-red-600 mb-4" weight="thin" />
              <h1 className="font-serif text-3xl mb-3">Invalid link</h1>
              <p className="text-sm text-neutral-600 mb-6">The reset link is missing its token. Request a new one below.</p>
              <Link to="/forgot-password" className="inline-block py-3 px-6 bg-[#001588] text-white tracking-wide hover:bg-black">Request new link</Link>
            </div>
          ) : done ? (
            <div data-testid="reset-password-success">
              <CheckCircle size={40} className="text-[#001588] mb-4" weight="thin" />
              <div className="overline text-[#001588] mb-3">Password updated</div>
              <h1 className="font-serif text-3xl mb-3">You&apos;re all set.</h1>
              <p className="text-sm text-neutral-600 mb-6">Redirecting to sign-in…</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-password-form">
              <div className="overline text-[#001588] mb-3">Choose a new password</div>
              <h1 className="font-serif text-3xl mb-3 text-[#050d19]">New password</h1>
              <p className="text-sm text-neutral-600 mb-6 leading-relaxed">
                Must be at least 8 characters. Use a mix of letters, numbers and symbols.
              </p>
              <label className="block mb-4">
                <span className="overline text-neutral-500 block mb-2">New password</span>
                <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                       className="w-full bg-white border border-[#e5e7eb] px-4 py-3 focus:outline-none focus:border-[#001588]"
                       data-testid="reset-password" autoComplete="new-password" />
              </label>
              <label className="block mb-6">
                <span className="overline text-neutral-500 block mb-2">Confirm password</span>
                <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                       className="w-full bg-white border border-[#e5e7eb] px-4 py-3 focus:outline-none focus:border-[#001588]"
                       data-testid="reset-confirm" autoComplete="new-password" />
              </label>
              <button type="submit" disabled={busy}
                      className="w-full py-3 bg-[#001588] text-white tracking-wide hover:bg-black transition-colors disabled:opacity-50"
                      data-testid="reset-submit">
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
